import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Box as DarkBox, SidePanel } from '@hivewatch/dark'

const ACTIVE_INCIDENTS_BAR_CLEARANCE = '42px'

export interface DarkFloorPlanSidePanelIProps {
  children: React.ReactNode
  header?: React.ReactNode
  footer?: React.ReactNode
  isExpanded: boolean
  onToggleExpansion: () => void
  isBodyPadded?: boolean
  dataTestid?: string
}

/**
 * Rail container shared by the rebuilt floor-plan panels. Fixes the size,
 * colorScheme and collapse labels in one place — the same shape
 * DarkTourPlansSidePanel gives the Tour Plans views — and sits at the feature
 * root rather than inside one panel because Floors, Devices and Checkpoints all
 * render it.
 */
export const DarkFloorPlanSidePanel = ({
  children,
  header,
  footer,
  isExpanded,
  onToggleExpansion,
  isBodyPadded = true,
  dataTestid = 'FloorPlanSidePanel',
}: DarkFloorPlanSidePanelIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()

  // The footer is pinned to the bottom when there is one, so it carries the
  // clearance; otherwise the body scrolls to the bottom and carries it.
  const clearedFooter = footer ? (
    <DarkBox pb={ACTIVE_INCIDENTS_BAR_CLEARANCE} width='100%'>
      {footer}
    </DarkBox>
  ) : undefined

  return (
    <SidePanel
      collapseLabel={t('floors:plan_panel_collapse_aria')}
      colorScheme={colorScheme}
      data-testid={dataTestid}
      expandLabel={t('floors:plan_panel_expand_aria')}
      footer={clearedFooter}
      header={header}
      isBodyPadded={isBodyPadded}
      isExpanded={isExpanded}
      onToggle={onToggleExpansion}
      size='sm'
    >
      {children}
      {!footer && <DarkBox pb={ACTIVE_INCIDENTS_BAR_CLEARANCE} />}
    </SidePanel>
  )
}
